// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

// Create the context
const AuthContext = createContext();

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // API base URL - use empty string in dev so requests go through React proxy on port 3000
  const API_BASE_URL = process.env.REACT_APP_API_URL || `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5001`;

  // Initialize auth token from localStorage on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    
    if (token && storedUser) {
      setAuthToken(token);
      try {
        const parsedUser = JSON.parse(storedUser);
        setCurrentUser(parsedUser);
      } catch (e) {
        console.error('Error parsing stored user:', e);
      }
    }
    setLoading(false);
  }, []);

  // --- 1. AXIOS INSTANCE ---
  const authApi = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true
  });

  // Attach token to every request automatically
  authApi.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Handle 401 (Unauthorized) globally
  authApi.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        console.log('Session expired or invalid token');
        logout();
      }
      return Promise.reject(error);
    }
  );

  // --- 2. DATA NORMALIZATION (CRITICAL FOR ROLES) ---
  const normalizeUserData = (userData) => {
    if (!userData) return null;
    
    let role = 'user';
    let is_admin = false;
    
    // Priority 1: Direct Role String
    if (userData.role) {
      role = userData.role;
      // 'admin' OR 'super_admin' counts as is_admin for backward compatibility
      is_admin = (userData.role === 'admin' || userData.role === 'super_admin');
    } 
    // Priority 2: Legacy Boolean
    else if (userData.is_admin !== undefined) {
      is_admin = Boolean(userData.is_admin);
      role = is_admin ? 'admin' : 'user';
    } 
    
    return {
      ...userData,
      role,     // 'user', 'admin', or 'super_admin'
      is_admin,  // true/false
      permissions: Array.isArray(userData.permissions) && userData.permissions.length ? userData.permissions : ['full'],
      parent_admin_id: userData.parent_admin_id || null
    };
  };

  // --- 3. CORE ACTIONS ---

  // LOGIN
  const login = async (email, password) => {
    try {
      console.log('Attempting login to:', `${API_BASE_URL}/api/auth/login`);
      
      const response = await authApi.post('/api/auth/login', { email, password });
      const { token, user } = response.data;
      
      const normalizedUser = normalizeUserData(user);
      
      // Store
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(normalizedUser));
      
      // Update State
      setCurrentUser(normalizedUser);
      setAuthToken(token);
      
      return { 
        success: true, 
        user: normalizedUser,
        role: normalizedUser.role,
        isAdmin: normalizedUser.is_admin
      };
    } catch (error) {
      console.error('Login error:', error);
      return { 
        success: false, 
        message: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  // REGISTER
  const register = async (username, email, password) => {
    try {
      // Note: If you are using OTP flow, this might redirect or act differently
      const response = await authApi.post('/api/auth/send-otp', { username, email, password });
      
      // If your flow returns a token immediately (old way):
      if (response.data.token) {
          const { token, user } = response.data;
          const normalizedUser = normalizeUserData(user);
          localStorage.setItem('token', token);
          localStorage.setItem('user', JSON.stringify(normalizedUser));
          setCurrentUser(normalizedUser);
          setAuthToken(token);
          return { success: true, user: normalizedUser };
      }

      // If OTP flow (new way):
      return { success: true, message: response.data.message };

    } catch (error) {
      console.error('Registration error:', error);
      return { 
        success: false, 
        message: error.response?.data?.error || 'Registration failed' 
      };
    }
  };

  // LOGOUT
  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('gameRedirect');
    setAuthToken(null);
    setCurrentUser(null);
    // Optional: Redirect to login
    // window.location.href = '/login'; 
  };

  // UPDATE USER (Local update helper)
  const updateUser = (updatedUserData) => {
    const normalizedUser = normalizeUserData({ ...currentUser, ...updatedUserData });
    setCurrentUser(normalizedUser);
    localStorage.setItem('user', JSON.stringify(normalizedUser));
  };

  // VERIFY TOKEN (On App Load)
  const verifyToken = async () => {
    const token = localStorage.getItem('token');

    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // We use /me endpoint to get the freshest user data (including role changes)
      const response = await authApi.get('/api/auth/me'); 
      
      if (response.data.success) {
        const normalizedUser = normalizeUserData(response.data.user);
        setCurrentUser(normalizedUser);
        setAuthToken(token);
        localStorage.setItem('user', JSON.stringify(normalizedUser));
      } else {
        throw new Error("Validation Failed");
      }
    } catch (error) {
      // Token is invalid/expired (401) or could not be verified — log the user out
      // completely so no stale user data or balance remains visible on refresh/entry.
      console.log('Token validation failed, logging out completely:', error?.response?.status || error?.message);
      logout();
    } finally {
      setLoading(false);
    }
  };

  // --- 4. ROLE HELPERS ---

  const isAdmin = () => {
    if (!currentUser) return false;
    return currentUser.role === 'admin' || currentUser.role === 'super_admin' || currentUser.is_admin === true;
  };

  const isSuperAdmin = () => {
    if (!currentUser) return false;
    return currentUser.role === 'super_admin';
  };

  const canAccessAdmin = () => isAdmin(); // Legacy alias

  // Get current token - EXPOSED FOR OTHER CONTEXTS
  const getToken = () => {
    return localStorage.getItem('token') || authToken;
  };

  useEffect(() => {
  const handleSessionEnded = (event) => {
    console.log('🔌 Session ended event received:', event.detail);
    // If our own game window (separate tab) opened a new connection, the
    // server ends this lobby socket — but we must NOT log out the lobby.
    if (event.detail?.reason === 'new_connection' && window.__gameWindowOpened) {
      console.log('Game window opened — keeping lobby session alive');
      return;
    }
    logout(); // This will properly clear everything
    // Optionally redirect to login
    window.location.href = '/login';
  };

  const handleRedirectToGame = (event) => {
    console.log('🎮 Redirect to game event:', event.detail);
    // Store redirect data
    localStorage.setItem('gameRedirect', JSON.stringify(event.detail));
  };

  window.addEventListener('session-ended', handleSessionEnded);
  window.addEventListener('redirect-to-game', handleRedirectToGame);

  return () => {
    window.removeEventListener('session-ended', handleSessionEnded);
    window.removeEventListener('redirect-to-game', handleRedirectToGame);
  };
}, [logout]);

  // --- INACTIVITY TIMEOUT (auto logout after inactivity) ---
  useEffect(() => {
    if (!currentUser) return; // Only track inactivity while logged in

    const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes of no activity
    let inactivityTimer = null;

    const resetInactivityTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => {
        console.log('⏰ Inactivity timeout reached — logging out completely');
        logout();
        window.location.href = '/login';
      }, INACTIVITY_TIMEOUT_MS);
    };

    // Any of these user interactions reset the inactivity timer
    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'wheel'];
    activityEvents.forEach((evt) => window.addEventListener(evt, resetInactivityTimer, { passive: true }));

    resetInactivityTimer();

    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      activityEvents.forEach((evt) => window.removeEventListener(evt, resetInactivityTimer));
    };
  }, [currentUser]);

  // --- 5. INITIALIZATION ---
  useEffect(() => {
    verifyToken();
  }, []);

  // --- 6. DEBUG HELPER ---
  const verifyAuthSetup = () => {
    console.log('=== AUTH DEBUG ===');
    console.log('Current User:', currentUser);
    console.log('Token:', getToken());
    console.log('Is Admin:', isAdmin());
    console.log('Is Super Admin:', isSuperAdmin());
    console.log('==================');
  };

  const value = {
    // State
    currentUser,
    token: getToken(), // Expose token for WalletContext
    authToken: getToken(), // Alias for backward compatibility
    loading,
    
    // Actions
    login,
    register,
    logout,
    updateUser,
    
    // Role helpers
    isAdmin,
    isSuperAdmin,
    canAccessAdmin,
    
    // API
    authApi, // Expose axios instance
    API_BASE_URL,
    
    // Utils
    verifyAuthSetup,
    getToken
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;